import { ORDER_STATUS_LABEL, centsToDecimal, formatCad, nextStatuses, type OrderStatus } from '@nsd/core';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ErrorBox, Loading, Money, Panel, Status, api, useAction, useLoad, when } from '../ui.tsx';

const ACTION_LABEL: Partial<Record<OrderStatus, string>> = {
  paid: 'Mark paid',
  processing: 'Mark processing',
  shipped: 'Mark shipped',
  completed: 'Mark completed',
  cancelled: 'Cancel order',
  refunded: 'Mark refunded',
};
const CARRIERS = ['Canada Post', 'Purolator', 'UPS', 'FedEx', 'Canpar', 'Other'];

function copy(text: string) {
  void navigator.clipboard?.writeText(text);
}

export function OrderDetail() {
  const number = Number(useParams()['number']);
  const { data, error, loading, reload } = useLoad(() => api.order(number), [number]);
  const action = useAction();
  const [note, setNote] = useState('');
  const [ship, setShip] = useState({ carrier: 'Canada Post', trackingNumber: '', trackingUrl: '' });

  if (loading && !data) return <Loading />;
  if (!data) return <ErrorBox error={error} />;
  const { order: o, notes } = data;
  const a = o.shippingAddress;
  const addressText = [`${a.firstName} ${a.lastName}`, a.company, a.address1, a.address2, `${a.city} ${a.province}  ${a.postalCode}`, a.phone].filter(Boolean).join('\n');

  const setStatus = (to: OrderStatus) => {
    if (to === 'cancelled' && !confirm(`Cancel order #${o.number}? Tracked stock goes back on the shelf.`)) return;
    void action.run(() => api.setStatus(o.number, to)).then((ok) => {
      if (ok) void reload();
    });
  };
  const addShipment = (e: FormEvent) => {
    e.preventDefault();
    const s = { carrier: ship.carrier, trackingNumber: ship.trackingNumber, ...(ship.trackingUrl ? { trackingUrl: ship.trackingUrl } : {}) };
    void action.run(() => api.addShipment(o.number, s)).then((ok) => {
      if (ok) void reload();
    });
  };
  const addNote = (e: FormEvent) => {
    e.preventDefault();
    if (!note.trim()) return;
    void action.run(() => api.addNote(o.number, note)).then((ok) => {
      if (ok) {
        setNote('');
        void reload();
      }
    });
  };

  return (
    <>
      <p><Link to="/orders">Orders</Link></p>
      <div className="titlebar">
        <h1>Order #{o.number}</h1>
        <Status status={o.status} />
      </div>
      <p className="muted">
        Placed {when(o.createdAt)}
        {o.source === 'import' && ' · imported from WordPress'}
      </p>
      <ErrorBox error={action.error} />
      <div className="actions">
        {nextStatuses(o.status).map((s) => (
          <button key={s} className={`btn ${s === 'cancelled' || s === 'refunded' ? 'btn--ghost' : ''}`} disabled={action.busy} onClick={() => setStatus(s)}>
            {ACTION_LABEL[s] ?? ORDER_STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="cols">
        <div>
          <Panel title="e-Transfer">
            <dl className="dl">
              <dt>Amount</dt>
              <dd>
                <Money cents={o.totalCents} /> <button className="mini" onClick={() => copy(centsToDecimal(o.totalCents))}>Copy</button>
              </dd>
              <dt>Message</dt>
              <dd>{o.etransfer.reference}</dd>
              <dt>Sent to</dt>
              <dd>{o.etransfer.recipientEmail}</dd>
              <dt>Security answer</dt>
              <dd>
                {o.etransfer.answer ? (
                  <>
                    <span className="answer">{o.etransfer.answer}</span> <button className="mini" onClick={() => copy(o.etransfer.answer!)}>Copy</button>
                    <small className="muted"> {when(o.etransfer.answerReceivedAt)}</small>
                  </>
                ) : (
                  <span className="muted">Not received yet</span>
                )}
              </dd>
            </dl>
          </Panel>

          <Panel title="Items">
            <table className="table">
              <tbody>
                {o.items.map((i) => (
                  <tr key={i.productId + i.name}>
                    <td>{i.name}<br /><small className="muted">{i.sku}</small></td>
                    <td>× {i.qty}</td>
                    <td className="num"><Money cents={i.lineCents} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td colSpan={2}>Subtotal</td><td className="num"><Money cents={o.subtotalCents} /></td></tr>
                {o.discountCents > 0 && <tr><td colSpan={2}>Free cartons {o.freeCartons ? `(${o.freeCartons})` : ''}</td><td className="num">-{formatCad(o.discountCents)}</td></tr>}
                <tr><td colSpan={2}>Shipping</td><td className="num">{o.shippingCents ? formatCad(o.shippingCents) : 'Free'}</td></tr>
                <tr className="grand"><td colSpan={2}>Total</td><td className="num"><Money cents={o.totalCents} /></td></tr>
              </tfoot>
            </table>
            {o.customerNote && <div className="notice notice--info">Customer note: {o.customerNote}</div>}
          </Panel>
        </div>

        <div>
          <Panel title="Ship to" actions={<button className="mini" onClick={() => copy(addressText)}>Copy address</button>}>
            <pre className="address">{addressText}</pre>
            <p>
              <Link to={`/customers/${encodeURIComponent(o.email)}`}>{o.email}</Link>
            </p>
          </Panel>

          <Panel title="Shipment">
            {o.shipment ? (
              <p>
                {o.shipment.carrier}: <strong>{o.shipment.trackingUrl ? <a href={o.shipment.trackingUrl} target="_blank" rel="noreferrer">{o.shipment.trackingNumber}</a> : o.shipment.trackingNumber}</strong>
                <br />
                <small className="muted">{when(o.shipment.shippedAt)}</small>
              </p>
            ) : (
              <form onSubmit={addShipment} className="form">
                <label>
                  Carrier
                  <select value={ship.carrier} onChange={(e) => setShip({ ...ship, carrier: e.target.value })}>
                    {CARRIERS.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </label>
                <label>
                  Tracking number
                  <input required value={ship.trackingNumber} onChange={(e) => setShip({ ...ship, trackingNumber: e.target.value })} />
                </label>
                <label>
                  Tracking link (optional)
                  <input type="url" value={ship.trackingUrl} onChange={(e) => setShip({ ...ship, trackingUrl: e.target.value })} />
                </label>
                <button className="btn" disabled={action.busy}>Save and mark shipped</button>
              </form>
            )}
          </Panel>

          <Panel title="Notes">
            <ul className="notes">
              {notes.map((n) => (
                <li key={n.at}>
                  <small className="muted">{when(n.at)} · {n.author}</small>
                  <div>{n.text}</div>
                </li>
              ))}
            </ul>
            <form onSubmit={addNote} className="form form--inline">
              <input placeholder="Add a note" value={note} onChange={(e) => setNote(e.target.value)} />
              <button className="btn" disabled={action.busy}>Add</button>
            </form>
          </Panel>
        </div>
      </div>
    </>
  );
}
